import { useEffect, useState, useRef } from "react";
import { TabPanel, TabView } from "primereact/tabview";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import ScenarioTable from "./ScenarioTable";
import ScenarioLine from "./ScenarioLine/ScenarioLine";
import { usePlanningStore } from "../../stores/scenarioPlanningStore";
import { useParams } from "react-router-dom";

const ScenarioPlanning = () => {
	const {
		listOfScenarios,
		getListOfScenarios,
		createScenarios,
		fetchScenarioTableData,
		scenarioTable,
		scenarioCpvSummary,
		getScenarioCpvSummary,
		updateBulkAllocations,
		deleteScenario,
		resetData,
	} = usePlanningStore();

	const { negotiation_id } = useParams<{ negotiation_id?: string }>();
	const [activeIndex, setActiveIndex] = useState(0);
	const [first, setFirst] = useState(0);
	const [rows, setRows] = useState(100);
	const [buttonLeft, setButtonLeft] = useState(0);
	const tabViewRef = useRef<HTMLDivElement>(null);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [scenarioToDelete, setScenarioToDelete] = useState<string | null>(null);

	// Load scenarios, create if needed, then fetch table for active tab
	useEffect(() => {
		let cancelled = false;

		(async () => {
			if (!negotiation_id) return;

			// 1) Try to get scenarios
			let scenarios = await getListOfScenarios(negotiation_id);

			// 2) If empty or falsy, create them
			if (!scenarios || scenarios.length === 0) {
				await createScenarios(negotiation_id);
				scenarios = await getListOfScenarios(negotiation_id);
			}

			if (cancelled) return;

			// 3) Fetch CPV summary for the slider visualization
			await getScenarioCpvSummary(Number(negotiation_id));

			// 4) If we have scenarios now, fetch table for the current tab
			if (scenarios && scenarios.length > 0) {
				// Clamp activeIndex just in case
				const index = Math.min(activeIndex, scenarios.length - 1);
				const scenarioName = scenarios[index];
				await fetchScenarioTableData(
					negotiation_id,
					scenarioName,
					1,
					100
				);
			}
		})();

		return () => {
			cancelled = true;
			resetData();
		};
		// run when negotiation_id changes; activeIndex is handled by onTabChange below
	}, [
		negotiation_id,
		getListOfScenarios,
		createScenarios,
		fetchScenarioTableData,
		getScenarioCpvSummary,
	]);

	const addScenario = async () => {
		if (!negotiation_id) return;

		const result = await createScenarios(negotiation_id);

		// Only refresh if scenario creation was successful
		if (result) {
			await getListOfScenarios(negotiation_id);
			// Refresh the CPV summary to include the new scenario
			await getScenarioCpvSummary(Number(negotiation_id));
		}
	};

	// pagination code
	useEffect(() => {
		if (!scenarioTable) return;
		const { page, page_size } = scenarioTable;
		if (typeof page === "number" && typeof page_size === "number") {
			setFirst((page - 1) * page_size);
			setRows(page_size);
		}
	}, [scenarioTable?.page, scenarioTable?.page_size]);

	// Calculate button position after tabs render
	useEffect(() => {
		const updateButtonPosition = () => {
			if (tabViewRef.current && listOfScenarios && listOfScenarios.length > 0) {
				// Find the tab navigation list
				const navElement = tabViewRef.current.querySelector('.p-tabview-nav');
				if (navElement) {
					const lastTab = navElement.children[listOfScenarios.length - 1] as HTMLElement;
					if (lastTab) {
						const rect = lastTab.getBoundingClientRect();
						const containerRect = tabViewRef.current.getBoundingClientRect();
						setButtonLeft(rect.right - containerRect.left + 12);
					}
				}
			}
		};

		// Initial calculation
		updateButtonPosition();

		// Recalculate after a short delay to ensure tabs are fully rendered
		const timer = setTimeout(updateButtonPosition, 100);

		return () => clearTimeout(timer);
	}, [listOfScenarios, activeIndex, scenarioCpvSummary]);

	const handleTabChange = async (e: { index: number }) => {
		setActiveIndex(e.index);
		setFirst(0);
		await fetchPage(e.index, 0, rows);
	};
	const fetchPage = async (idx = activeIndex, f = first, r = rows) => {
		const scenarios = listOfScenarios ?? [];
		if (!scenarios.length) return;
		const scenarioName = scenarios[Math.min(idx, scenarios.length - 1)];
		const pageNo = Math.floor(f / r) + 1; // convert 0-based offset -> 1-based page
		await fetchScenarioTableData(negotiation_id, scenarioName, pageNo, r);
	};

	const handlePage = async (e: { first: number; rows: number }) => {
		setFirst(e.first);
		setRows(e.rows);
		await fetchPage(activeIndex, e.first, e.rows);
	};

	const onUpdatingVol = async (changes: Array<{ tender_scenario_id: number; allocation_pct: number }>, isRecommended: boolean) => {
		if (!negotiation_id) return;

		const scenarioName = scenarioTable?.scenario_name;
		await updateBulkAllocations(negotiation_id, scenarioName, changes, isRecommended);
		// need to fetch table and CPV summary again after update
		await Promise.all([
			fetchPage(),
			getScenarioCpvSummary(Number(negotiation_id))
		]);
	};

	const handleDeleteScenario = async () => {
		if (!negotiation_id || !scenarioTable?.scenario_name) return;

		const scenarioName = scenarioTable.scenario_name;

		// Show confirmation dialog
		setScenarioToDelete(scenarioName);
		setShowDeleteDialog(true);
	};

	const confirmDelete = async () => {
		if (!negotiation_id || !scenarioToDelete) return;

		const result = await deleteScenario(Number(negotiation_id), scenarioToDelete);

		if (result) {
			// Refresh scenario list and CPV summary
			await getListOfScenarios(negotiation_id);
			await getScenarioCpvSummary(Number(negotiation_id));

			// Switch to the first tab
			setActiveIndex(0);
			await fetchPage(0, 0, rows);
		}

		// Close dialog
		setShowDeleteDialog(false);
		setScenarioToDelete(null);
	};

	const cancelDelete = () => {
		setShowDeleteDialog(false);
		setScenarioToDelete(null);
	};

	return (
		<div className="simple-tabs">
			{scenarioCpvSummary && scenarioCpvSummary.scenarios && scenarioCpvSummary.scenarios.length  &&
				<ScenarioLine scenarios={scenarioCpvSummary.scenarios} />
			}

			<div style={{ position: "relative" }} ref={tabViewRef}>
				<TabView
					activeIndex={activeIndex}
					onTabChange={handleTabChange}
					className="overflow-visible scenario-tabs-with-add-button"
				>
				{Array.isArray(listOfScenarios) &&
					listOfScenarios.map((name: string, i: number) => {
						// Find the current scenario's is_recommended flag
						// Note: scenarioCpvSummary renames recommended scenarios to "Recommended"
						// First try to find by exact name match
						let currentScenario = scenarioCpvSummary?.scenarios?.find(
							s => s.scenario_name === name
						);

						// If not found and there's a "Recommended" scenario, this might be it
						// (the backend renames the recommended scenario to "Recommended")
						if (!currentScenario) {
							const recommendedScenario = scenarioCpvSummary?.scenarios?.find(
								s => s.scenario_name === "Recommended" && s.is_recommended
							);
							// Only use it if we haven't matched it to another tab yet
							// We can check this by seeing if any previous scenario name matched
							const recommendedAlreadyMatched = listOfScenarios.slice(0, i).some(prevName =>
								scenarioCpvSummary?.scenarios?.some(s => s.scenario_name === prevName && s.is_recommended)
							);
							if (recommendedScenario && !recommendedAlreadyMatched) {
								currentScenario = recommendedScenario;
							}
						}

						const isRecommended = currentScenario?.is_recommended ?? false;

						// Append "(Recommended)" to tab header if this scenario is recommended
						const tabHeader = isRecommended ? `${name} (Recommended)` : name;

						return (
							<TabPanel header={tabHeader} key={name ?? i}>
								<ScenarioTable
									products={scenarioTable}
									first={first}
									rows={rows}
									onPage={handlePage}
									updateVolumeAllocation={onUpdatingVol}
									currentIsRecommended={isRecommended}
									onDeleteScenario={handleDeleteScenario}
								/>
							</TabPanel>
						);
					})}
				</TabView>
				{buttonLeft > 0 && (
					<button
						onClick={addScenario}
						title="Add Scenario"
						style={{
							position: "absolute",
							top: "8px",
							left: `${buttonLeft}px`,
							width: "32px",
							height: "32px",
							minWidth: "32px",
							minHeight: "32px",
							borderRadius: "6px",
							border: "1px solid var(--color-blue-500)",
							backgroundColor: "var(--color-blue-500)",
							color: "white",
							fontSize: "20px",
							lineHeight: "1",
							fontWeight: "bold",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							transition: "background-color 0.2s ease, border-color 0.2s ease",
							zIndex: 1,
							pointerEvents: "auto",
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.backgroundColor = "var(--color-blue-500)";
							e.currentTarget.style.borderColor = "var(--color-blue-500)";
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.backgroundColor = "var(--color-blue-500)";
							e.currentTarget.style.borderColor = "var(--color-blue-500)";
						}}
					>
						+
					</button>
				)}
			</div>

			{/* Delete Confirmation Dialog */}
			<Dialog
				header="Delete Scenario"
				visible={showDeleteDialog}
				onHide={cancelDelete}
				modal
				style={{ width: '450px' }}
				footer={
					<div className="flex justify-end gap-2">
						<Button
							label="Cancel"
							severity="secondary"
							outlined
							onClick={cancelDelete}
						/>
						<Button
							label="Delete"
							severity="danger"
							onClick={confirmDelete}
						/>
					</div>
				}
			>
				<div className="flex align-items-center gap-3">
					<i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: 'var(--status-warning)' }}></i>
					<div>
						<p className="m-0">
							Are you sure you want to delete the scenario <strong>"{scenarioToDelete}"</strong>?
						</p>
						<p className="mt-2 mb-0" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
							This action cannot be undone.
						</p>
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default ScenarioPlanning;
